import { zodResolver } from "@hookform/resolvers/zod";
import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { usePartyMutation } from "Api/Mutations/Party/usePartyMutation";
import { expiringRelatedPartiesKeys } from "Api/Queries/Party/useExpiringRelatedPartiesQuery";
import { getPartyQueryKey } from "Api/Queries/Party/usePartyQuery";
import { useCreatePartyWithRelation } from "Hooks/useCreatePartyWithRelation";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { FormSection } from "Components/Shared/FormSection";
import { CheckboxInput } from "Components/Shared/Inputs/Form/CheckboxInput";
import { DateFormInput } from "Components/Shared/Inputs/Form/DateFormInput";
import { Dropdown } from "Components/Shared/Inputs/Form/Dropdown";
import { AddressInput } from "Components/Shared/Inputs/Form/AddressInput";
import {
  AddressUncommittedProvider,
  useAddressUncommittedTracker,
} from "Components/Shared/Inputs/Form/AddressUncommittedContext";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import {
  type BeneficialOwnerFormSchemaInput,
  type BeneficialOwnerFormData,
  getBeneficialOwnerSchema,
} from "Components/Shared/Merchant/BeneficialOwners/schema";
import { CountryDropdown } from "Components/Shared/Inputs/Form/Dropdowns/CountryDropdown";
import { useGenderList } from "Hooks/Options/useGenderList";
import {
  AddressType,
  type AddressSuggestionDto,
  type CountryCode,
  PartyType,
  type PhoneNumber,
  RelationType,
  VatStatus,
} from "Infrastructure/Api/Api";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import { NO_VAT_NUMBER } from "Schemas/vatNumberSchema";
import { copyAddressDtoToFormFields } from "Utils/AddressUtils";
import { EMPTY_PHONE } from "Utils/PhoneUtils";

const COMPONENT_RESOURCES = Resources.Components.Merchant.BeneficialOwners.Form;

type Props = {
  owningPartyID: string;
  beneficialOwner: Partial<BeneficialOwnerFormData>;
  /** Phone persisted on the edited party — the form has no phone field, so the update
   *  must round-trip the current value instead of overwriting it with an empty one. */
  currentPhone?: PhoneNumber;
  /** Same round-trip rule as the phone: further citizenships persisted on the edited party. */
  currentOtherNationalities?: Array<CountryCode>;
  onAfterSubmit?: (isSuccess: boolean) => void;
};

export const BeneficialOwnerForm = ({
  beneficialOwner,
  owningPartyID,
  currentPhone,
  currentOtherNationalities,
  onAfterSubmit,
}: Props) => {
  const { t } = useResource();
  const genderList = useGenderList();
  const queryClient = useQueryClient();

  const formMethods = useForm<
    BeneficialOwnerFormSchemaInput,
    unknown,
    BeneficialOwnerFormData
  >({
    resolver: zodResolver(getBeneficialOwnerSchema(t)),
    defaultValues: {
      ...beneficialOwner,
      address: beneficialOwner.address ?? {
        type: AddressType.Permanent,
      },
    },
  });
  const {
    handleSubmit,
    control,
    setValue,
    formState: { isDirty, errors },
  } = formMethods;

  const address = useWatch({ control, name: "address" });
  const { hasUncommittedAddress, uncommittedContextValue } =
    useAddressUncommittedTracker();

  const handleAcceptAddressSuggestion = (suggestion: AddressSuggestionDto) => {
    copyAddressDtoToFormFields(suggestion, (field, value) =>
      setValue(`address.${field}`, value, {
        shouldDirty: true,
        shouldValidate: true,
      }),
    );
  };

  const handleClearAddress = () => {
    copyAddressDtoToFormFields({}, (field, value) =>
      setValue(`address.${field}`, value, {
        shouldDirty: true,
        shouldValidate: true,
      }),
    );
  };
  const { postPartyWithRelation, isPending: isCreatePartyPending } =
    useCreatePartyWithRelation({
      owningPartyID,
      relationType: RelationType.BeneficialOwner,
      onSuccess: () => {
        toast.success(t(COMPONENT_RESOURCES.AddSuccess));
        onAfterSubmit?.(true);
        queryClient.invalidateQueries({
          queryKey: getPartyQueryKey(owningPartyID),
        });
        queryClient.invalidateQueries({
          queryKey: expiringRelatedPartiesKeys.all,
        });
      },
      onError: () => {
        toast.error(t(COMPONENT_RESOURCES.AddError));
        onAfterSubmit?.(false);
      },
    });

  const { mutate: updateParty, isPending: isUpdatePartyPending } =
    usePartyMutation({
      onSuccess: () => {
        toast.success(t(COMPONENT_RESOURCES.UpdateSuccess));
        onAfterSubmit?.(true);
        queryClient.invalidateQueries({
          queryKey: getPartyQueryKey(owningPartyID),
        });
        queryClient.invalidateQueries({
          queryKey: expiringRelatedPartiesKeys.all,
        });
      },
      onError: () => {
        toast.error(t(COMPONENT_RESOURCES.UpdateError));
        onAfterSubmit?.(false);
      },
    });

  const onSubmit = (data: BeneficialOwnerFormData) => {
    if (hasUncommittedAddress) {
      toast.error(t(Resources.Components.Shared.AddressInput.UncommittedQuery));
      return;
    }

    const permanentAddress = {
      ...data.address,
      type: AddressType.Permanent,
    };

    if (data.publicID) {
      updateParty({
        publicID: data.publicID,
        data: {
          ...data,
          type: PartyType.NaturalPerson,
          isForeigner: data.isForeigner || false,
          licenses: [],
          addresses: [permanentAddress],
          relatedParties: [],
          phone: currentPhone ?? EMPTY_PHONE,
          // Required by the DTO shape only — the server ignores them on updates.
          verifiedPhone: EMPTY_PHONE,
          vatStatus: VatStatus.Unknown,
          vatNumber: NO_VAT_NUMBER, // a natural person
          // The owner form offers no second citizenship; the edited party keeps its own.
          otherNationalities: currentOtherNationalities ?? [],
        },
      });
    } else {
      postPartyWithRelation({
        ...data,
        addresses: [permanentAddress],
        phone: EMPTY_PHONE,
        vatNumber: NO_VAT_NUMBER,
        otherNationalities: [],
      });
    }
  };

  const isPending = isCreatePartyPending || isUpdatePartyPending;

  return (
    <FormProvider {...formMethods}>
      <AddressUncommittedProvider value={uncommittedContextValue}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormSection legend={t(COMPONENT_RESOURCES.Legend)}>
            <Box
              display="flex"
              justifyContent="space-between"
              width="100%"
              gap={4}
            >
              <FormInput
                control={control}
                name="firstName"
                label={t(Resources.Common.BeneficialOwner.Name)}
                formControlClass="fullWidth"
              />
              <FormInput
                control={control}
                name="lastName"
                label={t(Resources.Common.BeneficialOwner.LastName)}
                formControlClass="fullWidth"
              />
            </Box>
            <Dropdown
              codeList={genderList}
              control={control}
              name="gender"
              label={t(Resources.Common.BeneficialOwner.Gender)}
            />
            <DateFormInput
              control={control}
              name="birthDate"
              label={t(Resources.Common.BeneficialOwner.BirthDate)}
            />
            <CountryDropdown
              control={control}
              name="nationality"
              label={t(Resources.Common.BeneficialOwner.Nationality)}
            />
            <Box mb={4}>
              <CheckboxInput
                control={control}
                name="isForeigner"
                label={t(Resources.Common.BeneficialOwner.IsForeigner)}
              />
            </Box>
          </FormSection>
          <FormSection legend={t(Resources.Common.Address.Title)}>
            <AddressInput
              address={address}
              onAcceptSuggestion={handleAcceptAddressSuggestion}
              onClear={handleClearAddress}
              hasErrors={!!errors.address}
            >
              <FormInput
                control={control}
                name="address.street"
                label={t(Resources.Common.Address.Street)}
              />
              <Box
                display="flex"
                justifyContent="space-between"
                width="100%"
                gap={4}
              >
                <FormInput
                  formControlClass="fullWidth"
                  control={control}
                  name="address.streetNumber"
                  label={t(Resources.Common.Address.StreetNumber)}
                />
                <FormInput
                  formControlClass="fullWidth"
                  control={control}
                  name="address.orientationNumber"
                  label={t(Resources.Common.Address.OrientationNumber)}
                />
              </Box>
              <FormInput
                control={control}
                name="address.municipality"
                label={t(Resources.Common.Address.Municipality)}
              />
              <FormInput
                control={control}
                name="address.postalCode"
                label={t(Resources.Common.Address.PostalCode)}
                mask="000 00"
              />
            </AddressInput>
          </FormSection>
          <PrimaryButton
            fullWidth
            type="submit"
            isLoading={isPending}
            variant="contained"
            disabled={!isDirty}
            color="primary"
          >
            {t(Resources.Common.Save)}
          </PrimaryButton>
        </form>
      </AddressUncommittedProvider>
    </FormProvider>
  );
};
